import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, setToken } from "@/lib/api";
import type { UserPublic } from "@/lib/types";

interface AuthContextValue {
  user: UserPublic | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string, phone: string, phone_country: string, referral_code?: string, otp_code?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<UserPublic | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(async () => {
    // Always attempt a credentialed /auth/me: the httpOnly `sb_auth` cookie can
    // authenticate the session even when no localStorage Bearer token exists
    // (cookie-first). We only clear state if the request actually fails.
    try {
      const { data } = await api.get<UserPublic>("/auth/me");
      setUser(data);
      // Fold any guest cart built while logged out into the user's cart.
      try {
        const { mergeGuestCartIfAny } = await import("@/lib/cartClient");
        const { queryClient } = await import("@/index");
        await mergeGuestCartIfAny();
        queryClient.invalidateQueries({ queryKey: ["marketplace-cart"] });
      } catch { /* non-blocking */ }
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me check if OAuth callback is in progress
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const _afterAuth = useCallback(async () => {
    try {
      const { mergeGuestCartIfAny } = await import("@/lib/cartClient");
      const { queryClient } = await import("@/index");
      await mergeGuestCartIfAny();
      queryClient.invalidateQueries({ queryKey: ["marketplace-cart"] });
    } catch { /* non-blocking */ }
  }, []);

  const login = async (email: string, password: string) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    await _afterAuth();
    return data.user;
  };

  const signup = async (name: string, email: string, password: string, phone: string, phone_country: string, referral_code?: string, otp_code?: string) => {
    const { getAffiliateCode } = await import("@/lib/affiliate");
    const affiliate_code = getAffiliateCode() || undefined;
    const { data } = await api.post("/auth/signup", { name, email, password, phone, phone_country, ...(referral_code ? { referral_code } : {}), ...(affiliate_code ? { affiliate_code } : {}), ...(otp_code ? { otp_code } : {}) });
    setToken(data.token);
    setUser(data.user);
    await _afterAuth();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
  };

  const loginWithToken = async (token: string, u?: any) => {
    setToken(token);
    if (u) setUser(u);
    // Refresh from /auth/me to get the canonical record
    await refresh();
    await _afterAuth();
  };

  const value = useMemo(
    () => ({ user, loading, login, signup, logout, refresh, loginWithToken }),
    [user, loading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
